import { useQuery } from '@tanstack/react-query'
import { getDemoEmployeeCount } from '../api/demo-customer.api'

export const DEMO_POPULATION_QUERY_KEY = (customerId: string) => [
  'demoPopulationStatus',
  customerId,
]

interface DemoPopulationStatus {
  /** Total number of employees seeded into this demo customer. */
  employeeCount: number
  /** True when at least one employee exists — mirrors the backend guard. */
  isPopulated: boolean
  /** True while the initial fetch is in flight. */
  isLoading: boolean
}

/**
 * Queries the member count for a demo customer so the population state
 * survives page reloads.
 *
 * Uses `POST /api/v1/members/search` with `limit: 1` — only the `total`
 * field matters, not the rows.  Query key is
 * `['demoPopulationStatus', customerId]` so `useUploadAndPopulateDemoQuote`
 * can invalidate it immediately after a successful populate.
 */
export function useDemoPopulationStatus(
  customerId: string
): DemoPopulationStatus {
  const { data, isLoading } = useQuery({
    queryKey: DEMO_POPULATION_QUERY_KEY(customerId),
    queryFn: () => getDemoEmployeeCount(customerId),
    // A count of 0 is a valid result — don't treat it as falsy.
    select: (total) => total,
    // Stale time: 5 minutes. The count only changes after a populate run,
    // and the hook invalidates immediately on success, so this just avoids
    // unnecessary background refetches while the page is open.
    staleTime: 5 * 60 * 1000,
  })

  const employeeCount = data ?? 0

  return {
    employeeCount,
    isPopulated: employeeCount > 0,
    isLoading,
  }
}
